import React, { useCallback } from 'react';
import { ClinicAvatar } from 'shared/components/Avatars';
import Tooltip from 'shared/components/Tooltip';
import { ChannelViewChannelMessageFragment } from 'shared/types/graphql';
import styled from 'styled-components/macro';
import { format } from 'date-fns';
import { EyeIcon } from 'assets/icons';
import useFormSubmission from 'shared/hooks/useFormSubmission';

interface IAppointmentRequestMessageProps {
  message: ChannelViewChannelMessageFragment;
  title: string;
  subtitle: string;
  clinicPetParentId: string;
}

const AppointmentRequestMessage = ({
  message,
  title,
  subtitle,
  clinicPetParentId,
}: IAppointmentRequestMessageProps): JSX.Element | null => {
  const attachment = message.attachments && message.attachments[0];
  const formSubmissionId = attachment?.entityId;

  const { data: formSubmission, openFormSubmissionEditor } = useFormSubmission({ formSubmissionId });

  const formSubmissionContent =
    typeof formSubmission?.completedContent === 'string'
      ? JSON.parse(formSubmission?.completedContent)
      : formSubmission?.completedContent;
  const formQuestions = formSubmissionContent?.questions;

  const onOpenFormSubmissionEditor = useCallback((): void => {
    if (!formSubmissionId) return;
    openFormSubmissionEditor(clinicPetParentId);
  }, [clinicPetParentId, formSubmissionId, openFormSubmissionEditor]);

  return (
    <AppointmentRequestMessageContainer>
      <Header>
        <LeftHeaderGroup>
          <AvatarContainer>
            <ClinicAvatar isAutomated />
          </AvatarContainer>
          <HeaderText>
            <WidgetRequestTitle>
              <div>{title}</div>
              {formSubmission?.submittedAt && (
                <Tooltip content={format(new Date(formSubmission?.submittedAt) || '', "cccc',' PP 'at' p")}>
                  <Timestamp>{format(new Date(formSubmission?.submittedAt) || '', 'h:mma')}</Timestamp>
                </Tooltip>
              )}
            </WidgetRequestTitle>
            <RequestTypeName>{subtitle}</RequestTypeName>
          </HeaderText>
        </LeftHeaderGroup>
        <RightHeaderGroup>
          <Tooltip content="View Original Form">
            <StyledEyeIcon onClick={onOpenFormSubmissionEditor} fill="currentColor" />
          </Tooltip>
        </RightHeaderGroup>
      </Header>
      <FormAnswers>
        {!!formSubmissionContent?.introMessage && (
          <Row>
            <Prompt>Intro Message</Prompt>
            <Answer>{formSubmissionContent.introMessage}</Answer>
          </Row>
        )}
        {formQuestions?.map((question: { prompt: string; answer: string[] | string }, index: number) => (
          <Row key={index}>
            <Prompt>{question.prompt}</Prompt>
            {Array.isArray(question?.answer) ? (
              question?.answer.map((individualAnswer: string, index: number) => (
                <Answer key={index}>{individualAnswer}</Answer>
              ))
            ) : (
              <Answer>{question.answer}</Answer>
            )}
          </Row>
        ))}
      </FormAnswers>
    </AppointmentRequestMessageContainer>
  );
};

const AppointmentRequestMessageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  width: 30rem;
`;

const Header = styled.div`
  display: flex;
  align-items: stretch;
  width: 100%;
  justify-content: space-between;
`;

const LeftHeaderGroup = styled.div`
  display: flex;
`;

const RightHeaderGroup = styled.div`
  display: flex;
  align-items: center;
`;

const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  margin-left: 8px;

  & > * {
    font-weight: 600;
    font-size: 16px;
  }
`;

const WidgetRequestTitle = styled.div`
  display: flex;
  align-items: baseline;
`;

const Timestamp = styled.div`
  color: hsl(216 85% 52%);
  font-size: 11.2px;
  font-weight: normal;
  margin-left: 6px;
  text-transform: lowercase;
`;

const RequestTypeName = styled.div`
  color: var(--chakra-colors-text-brand);
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: baseline;
`;

const StyledEyeIcon = styled(EyeIcon)`
  cursor: pointer;
  path {
    fill: var(--chakra-colors-text-brand);
  }
`;

const FormAnswers = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  margin-top: 24px;
  padding: 0 16px;
  border-left: 2px solid var(--chakra-colors-text-brand);
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;

  & + & {
    margin-top: 24px;
  }
`;

const Prompt = styled.div`
  font-weight: 600;
  color: #73747d;
  margin-bottom: 4px;
`;

const Answer = styled.div`
  white-space: pre-wrap;
  line-height: 1.4;

  ul {
    padding-inline-start: 20px;
  }
`;

export default AppointmentRequestMessage;
